// Markdown → DOMQL children for the simone assistant.
//
// Registered as a project FUNCTION so the Chat message bubble renders markdown via
// `el.call('renderAssistantMarkdown', text)`. Shared via functions/index.js so docs,
// preview and workspace all render assistant markdown through ONE function.
//
// ── Why this looks the way it does (READ before refactoring) ──────────────────
// On the brender (docs.symbols.app) frank SERIALIZES this function and re-evals it
// in an isolated scope. In that runtime:
//   • MODULE-LEVEL code never runs — no top-level side effects, no module consts,
//     no hoisted helpers survive (they become undefined free vars → ReferenceError).
//     That's why every helper + the component map is INLINED in the body, and why a
//     module-init `window.X = …` assignment does NOT work here.
//   • An npm-import CALL can't be bundled into the serialized scope — frank rewrites
//     `transformMarkdown(…)` to `__scope.M(…)` and `__scope.M` is undefined → it is
//     NOT callable. (@symbo.ls/symbolize-markdown is an internal pkg, so it isn't
//     CDN-loadable at runtime either.)
// Net: symbolize-markdown's transformMarkdown is reachable in NORMAL bundles
// (workspace, preview) but NOT inside the serialized brender runtime.
//
// So this function is dual-mode and self-healing:
//   1. a runtime global window.__symbolizeMarkdown, if some host pre-warmed one;
//   2. else the bundled `transformMarkdown` import IF it's actually a function
//      (true in workspace/preview; on the brender `typeof` is 'undefined');
//   3. else a SELF-CONTAINED inline parser (no imports) so the brender still renders
//      real markdown.
// Same function everywhere, best renderer per environment. Framework fix to bundle
// npm scope refs into serialized fns is ticketed in tickets/smbls.md.
import { transformMarkdown } from '@symbo.ls/symbolize-markdown'

export const renderAssistantMarkdown = (body) => {
  let text = (body || '') + ''
  if (!text) return []

  // Defense-in-depth for a model that ignored the no-raw-JSON rule and
  // answered with a bare object/array (observed live 2026-08-11: a member
  // list and an architecture explanation rendered as walls of JSON prose).
  // Render it as a proper code block instead of paragraph soup — the
  // prompt-side rule is the real fix; this keeps the bubble legible when it
  // happens anyway.
  const trimmed = text.trim()
  if (/^[[{]/.test(trimmed)) {
    try {
      const parsed = JSON.parse(trimmed)
      if (parsed && typeof parsed === 'object') {
        return [
          {
            tag: 'pre',
            text: JSON.stringify(parsed, null, 2),
            whiteSpace: 'pre-wrap',
            overflow: 'auto',
            maxHeight: 'G',
            padding: 'Z A',
            round: 'radiusControl',
            theme: 'document',
            fontSize: '.9em'
          }
        ]
      }
    } catch (_) {
      /* not JSON — render as markdown */
    }
  }

  // Autolink bare URLs into markdown links BEFORE any renderer — neither the
  // transpiler nor the inline fallback tokenizes a bare https://…, so pasted
  // links rendered as dead text. Skipped whenever the text carries backticks
  // (a URL inside a code sample must stay verbatim).
  if (!text.includes('`')) {
    text = text.replace(
      /(^|\s)(https?:\/\/[^\s<>)]+[^\s<>).,;:!?'"])/g,
      (mm, pre, url) => `${pre}[${url}](${url})`
    )
  }

  // markdown node type → registered component key (symbolize path). Headings demote
  // to H6 so a chat bubble never claims page-level heading slots.
  const COMPONENTS = {
    em: 'Em',
    h1: 'H6',
    h2: 'H6',
    h3: 'H6',
    h4: 'H6',
    h5: 'H6',
    h6: 'H6',
    hr: 'Hr',
    li: 'LI',
    ol: 'OL',
    ul: 'UL',
    del: 'Del',
    img: 'Img',
    pre: 'Pre',
    code: 'InlineCode',
    link: 'Link',
    strong: 'Strong',
    codeBlock: 'CodeBlock',
    paragraph: 'P',
    blockquote: 'Blockquote'
  }

  // Pull assistant lists inside the bubble (no hanging bullets); recurse nested.
  const inset = (node) => {
    if (!node || typeof node !== 'object') return node
    const out = { ...node }
    const kind = out.extends || out.tag
    if (kind === 'UL' || kind === 'ul' || kind === 'OL' || kind === 'ol') {
      Object.assign(out, {
        listStylePosition: 'inside',
        padding: 'V 0',
        paddingLeft: '0',
        margin: 'V 0'
      })
    }
    if (Array.isArray(out.children)) out.children = out.children.map(inset)
    return out
  }

  // A node that renders child nodes has no text of its own, and says so. The
  // component keys above are BARE keys, so they resolve through the linked
  // libraries too, and a library's component may carry demo text: in
  // system/default `P`, `Caption` and `H1`–`H6` read "It was the last day for
  // our tribe, the year ends", `Link` reads "Link", `Strong` "Strong text".
  // A paragraph with inline marks has keyed children and no `text`, so it
  // showed that sentence before its own words (dev, system/hq, a note in CLI
  // mode, 2026-10-23); a link with bold text would show "Link". `text: null`
  // cancels an inherited text (measured with smbls create in jsdom).
  const ownTextOnly = (node) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return node
    const out = { ...node }
    let hasKids = false
    for (const k of Object.keys(out)) {
      const v = out[k]
      if (/^[A-Z]/.test(k) && v && typeof v === 'object' && !Array.isArray(v)) {
        hasKids = true
        out[k] = ownTextOnly(v)
      }
    }
    if (Array.isArray(out.children) && out.children.length) {
      hasKids = true
      out.children = out.children.map(ownTextOnly)
    }
    if (hasKids && out.text === undefined) out.text = null
    return out
  }

  // Resolve the real symbolize-markdown transform if it's reachable here.
  let tm = null
  if (typeof window !== 'undefined' && typeof window.__symbolizeMarkdown === 'function') {
    tm = window.__symbolizeMarkdown
  } else {
    // In the serialized brender runtime `transformMarkdown` is rewritten to an
    // undefined __scope ref — guard so the typeof check can't throw.
    try {
      if (typeof transformMarkdown === 'function') tm = transformMarkdown
    } catch (_) {
      tm = null
    }
  }

  if (tm) {
    try {
      const spec = tm(text, { components: COMPONENTS })
      if (spec && typeof spec === 'object') {
        // single node ({tag|extends|text|children}) vs keyed object → children array
        const arr =
          'extends' in spec || 'tag' in spec || 'text' in spec || 'children' in spec
            ? [spec]
            : Object.keys(spec)
                .filter((k) => !k.startsWith('__'))
                .map((k) => spec[k])
                .filter((v) => v && typeof v === 'object')
        if (arr.length) return arr.map((node) => ownTextOnly(inset(node)))
      }
    } catch (_) {
      /* fall through to the inline parser */
    }
  }

  // ── Self-contained inline parser (no imports → brender-safe) ──
  // Handles **bold**, *italic*, `code`, [links](…); # headings, ``` fences,
  // - / * bullet lists, > blockquotes, and paragraphs. Styling via literal
  // var(--color-X) (raw vars are brender-safe; bare color tokens are not).
  // A backslash before ASCII punctuation is that character, literally
  // (CommonMark backslash escapes): `\*` prints `*` and opens no emphasis,
  // `\#` at a line start is no heading. A code span keeps its backslashes.
  const unescapeMd = (s) => s.replace(/\\([!-/:-@[-`{-~])/g, '$1')
  const inline = (txt) => {
    const parts = []
    let plain = ''
    const flush = () => {
      if (plain) parts.push({ tag: 'span', text: plain })
      plain = ''
    }
    // Link hrefs accept ANY non-space target — the old https?:|/-rooted gate
    // left relative/anchor links (`[here](tickets/123)`) as literal text.
    const re =
      /\\([!-/:-@[-`{-~])|\*\*((?:\\.|[^*\\])+)\*\*|\*((?:\\.|[^*\\])+)\*|`([^`]+)`|\[((?:\\.|[^\]\\])+)\]\(((?:\\.|[^)\s\\])+)\)/g
    let last = 0
    let m
    while ((m = re.exec(txt))) {
      plain += txt.slice(last, m.index)
      last = re.lastIndex
      if (m[1] != null) {
        plain += m[1]
        continue
      }
      flush()
      if (m[2] != null) parts.push({ tag: 'strong', text: unescapeMd(m[2]) })
      else if (m[3] != null) parts.push({ tag: 'em', text: unescapeMd(m[3]) })
      else if (m[4] != null)
        parts.push({
          tag: 'code',
          text: m[4],
          padding: 'Y Z',
          round: 'radiusControl',
          theme: 'document',
          fontSize: '.92em'
        })
      else {
        const href = unescapeMd(m[6])
        const external = /^https?:\/\//i.test(href)
        parts.push({
          tag: 'a',
          text: unescapeMd(m[5]),
          href,
          ...(external ? { target: '_blank' } : {}),
          color: 'var(--color-blue)',
          textDecoration: 'underline'
        })
      }
    }
    plain += txt.slice(last)
    flush()
    return parts.length ? parts : [{ tag: 'span', text: txt }]
  }

  // GFM table row split — trims one optional leading/trailing pipe, then
  // splits on interior `|`. Shared by the header/separator/body detection
  // below (AIUX-MD-RENDER, tickets/sonnet.md — this self-contained parser
  // is the ONLY renderer on the brender, per the module note above, and was
  // silently missing table/hr support even though ChatMessage.js's `& table`
  // / `& th` / `& hr` descendant CSS already assumed both renderer paths
  // produced them — see the comment there).
  const splitTableRow = (line) => {
    let s = line.trim()
    if (s.startsWith('|')) s = s.slice(1)
    if (s.endsWith('|') && s.length) s = s.slice(0, -1)
    return s.split('|').map((c) => c.trim())
  }
  const isTableSeparator = (line) => {
    const cells = splitTableRow(line)
    return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c))
  }

  try {
    const lines = text.split('\n')
    const out = []
    let i = 0
    while (i < lines.length) {
      const ln = lines[i]
      if (/^\s*```/.test(ln)) {
        const code = []
        i++
        while (i < lines.length && !/^\s*```/.test(lines[i])) {
          code.push(lines[i])
          i++
        }
        i++
        out.push({
          tag: 'pre',
          text: code.join('\n'),
          whiteSpace: 'pre-wrap',
          overflow: 'auto',
          padding: 'Z A',
          round: 'radiusControl',
          theme: 'document',
          fontSize: '.9em'
        })
        continue
      }
      // GFM table — a header row immediately followed by a `---`/`:---:`
      // separator row of the SAME cell count. Checked before the generic
      // paragraph fallthrough (and before the `---`-only hr check below, so
      // a real separator row is never itself mistaken for a standalone hr —
      // it's consumed as part of the table).
      if (ln.includes('|') && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
        const header = splitTableRow(ln)
        const sepCells = splitTableRow(lines[i + 1])
        if (header.length && sepCells.length === header.length) {
          i += 2
          const rows = []
          while (i < lines.length && lines[i].trim() !== '' && lines[i].includes('|')) {
            rows.push(splitTableRow(lines[i]))
            i++
          }
          out.push({
            tag: 'table',
            children: [
              {
                tag: 'thead',
                children: [
                  { tag: 'tr', children: header.map((c) => ({ tag: 'th', children: inline(c) })) }
                ]
              },
              {
                tag: 'tbody',
                children: rows.map((r) => ({
                  tag: 'tr',
                  children: r.map((c) => ({ tag: 'td', children: inline(c) }))
                }))
              }
            ]
          })
          continue
        }
      }
      // Horizontal rule — a line of 3+ `-`/`*`/`_` and nothing else. Table
      // separator rows are already consumed above, so this only ever matches
      // a genuine standalone `---`.
      if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(ln)) {
        out.push({ tag: 'hr' })
        i++
        continue
      }
      const h = ln.match(/^\s*(#{1,6})\s+(.*)/)
      if (h) {
        out.push({ tag: 'h4', margin: 'Z 0 Y', children: inline(h[2]) })
        i++
        continue
      }
      if (/^\s*>\s?/.test(ln)) {
        const quote = []
        while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
          quote.push(lines[i].replace(/^\s*>\s?/, ''))
          i++
        }
        out.push({
          tag: 'blockquote',
          margin: 'Y 0',
          paddingLeft: 'A',
          borderLeft: '2px solid var(--color-line)',
          color: 'var(--color-caption)',
          children: inline(quote.join(' '))
        })
        continue
      }
      if (/^\s*[-*]\s+/.test(ln)) {
        const items = []
        while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
          items.push({
            tag: 'li',
            margin: '0 0 Y',
            children: inline(lines[i].replace(/^\s*[-*]\s+/, ''))
          })
          i++
        }
        out.push({
          tag: 'ul',
          listStylePosition: 'inside',
          padding: '0',
          margin: '0',
          children: items
        })
        continue
      }
      if (ln.trim() === '') {
        i++
        continue
      }
      const para = []
      while (
        i < lines.length &&
        lines[i].trim() !== '' &&
        !/^\s*(#{1,6})\s|^\s*```|^\s*[-*]\s|^\s*>\s?/.test(lines[i])
      ) {
        para.push(lines[i])
        i++
      }
      out.push({ tag: 'p', margin: '0 0 Z', lineHeight: '1.55', children: inline(para.join(' ')) })
    }
    return out.length ? out : [{ whiteSpace: 'pre-wrap', text }]
  } catch (_) {
    return [{ whiteSpace: 'pre-wrap', text }]
  }
}
